/**
 * Created by neo on 18.01.17.
 */

import { useQuery } from '@tanstack/react-query';
import { makeAutoObservable } from 'mobx';
import { v4 as UUID } from 'uuid';
import { MicroCycleDay, MicroCycleDayApi } from '../Coach/Program/MicroCycleDay';
import { CoachWorkout, CoachWorkoutApi } from '../Coach/Program/Workout/CoachWorkout';

export type WorkoutLogHeader = {
  id: string;
  athleteId: string;
  type: string;
  startDateTime?: Date;
  endDateTime?: Date;
  bmr: number;
  calories: number;
  workoutId?: string;
  coachProgramId?: string;
  mesoCycleId?: string;
  microCycleId?: string;
  microCycleDayId?: string;
  microCycleDay?: MicroCycleDay;
  workout?: CoachWorkout;
  readonly duration: number;
  setMicroCycleDay(day: MicroCycleDay): void;
  setWorkout(workout: CoachWorkout): void;
};

export function createWorkoutLogHeader(json?: Partial<WorkoutLogHeader>): WorkoutLogHeader {
  return makeAutoObservable({
    id: json?.id ?? UUID(),
    athleteId: json?.athleteId ?? '',
    type: json?.type ?? 'gym_strength',
    startDateTime: json?.startDateTime ? new Date(json.startDateTime as any) : undefined,
    endDateTime: json?.endDateTime ? new Date(json.endDateTime as any) : undefined,
    bmr: json?.bmr ?? 0,
    calories: json?.calories ?? 0,
    workout: json?.workout,
    workoutId: json?.workoutId ?? json?.workout?.id,
    microCycleDay: json?.microCycleDay,
    coachProgramId: json?.coachProgramId ?? json?.microCycleDay?.athleteSuperMacroId,
    mesoCycleId: json?.mesoCycleId ?? json?.microCycleDay?.mesoCycleId,
    microCycleId: json?.microCycleId ?? json?.microCycleDay?.microCycleId,
    microCycleDayId: json?.microCycleDayId ?? json?.microCycleDay?.id,

    get duration(): number {
      return (this.endDateTime?.valueOf() ?? Date.now()) - (this.startDateTime?.valueOf() ?? Date.now());
    },

    setMicroCycleDay(day: MicroCycleDay) {
      this.microCycleDay = day;
    },

    setWorkout(workout: CoachWorkout) {
      this.workout = workout;
    },
  });
}

/**
 * Hook to fetch MicroCycleDay for a WorkoutLogHeader
 */
export function useMicroCycleDay(microCycleDayId?: string) {
  return useQuery({
    queryKey: ['microCycleDay', microCycleDayId],
    queryFn: () => (microCycleDayId ? MicroCycleDayApi.findOne(microCycleDayId) : Promise.resolve(undefined)),
    enabled: !!microCycleDayId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch CoachWorkout for a WorkoutLogHeader
 */
export function useCoachWorkout(workoutId?: string) {
  return useQuery({
    queryKey: ['coachWorkout', workoutId],
    queryFn: () => (workoutId ? CoachWorkoutApi.get(workoutId) : Promise.resolve(undefined)),
    enabled: !!workoutId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Combined hook to fetch both MicroCycleDay and CoachWorkout for a WorkoutLogHeader
 */
export function useWorkoutLogHeaderData(header: WorkoutLogHeader) {
  const microCycleDayQuery = useMicroCycleDay(header.microCycleDayId);
  const workoutQuery = useCoachWorkout(header.workoutId);

  return {
    microCycleDay: microCycleDayQuery.data,
    workout: workoutQuery.data,
    isLoading: microCycleDayQuery.isLoading || workoutQuery.isLoading,
    error: microCycleDayQuery.error || workoutQuery.error,
  };
}
